// frontend/src/components/NavBar.js
import React, { useState, useRef, useCallback } from 'react';
import {
  AppBar,
  Toolbar,
  IconButton,
  Menu,
  MenuItem,
  Avatar,
  Box,
  Divider,
  Typography,
  ListItemText,
  Paper,
  MenuList,
  Grow
} from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import Popper from '@mui/material/Popper';
import { useNavigate } from 'react-router-dom';
import { isAdmin } from '../auth/token';
import BACKEND_URL from '../config';


const aiFeedbackSubItems = [
  { label: 'Human-in-the-Loop Training Statistics', path: '/TrainingActivity' },
  { label: 'Provide Feedback on a Conversation', path: '/ConversationReview' },
  { label: 'Provide Feedback on Category Values', path: '/CategorizationFeedback' },
];

const CLOSE_DELAY_MS = 150; // small but snappy

/** Top-level hover menu (Research) */
const useHoverMenu = () => {
  const buttonRef = useRef(null);
  const closeTimerRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [overTrigger, setOverTrigger] = useState(false);
  const [overMenu, setOverMenu] = useState(false);

  const clearCloseTimer = useCallback(() => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  }, []);

  const maybeClose = useCallback(() => {
    clearCloseTimer();
    if (!overTrigger && !overMenu) {
      closeTimerRef.current = setTimeout(() => setOpen(false), CLOSE_DELAY_MS);
    }
  }, [overTrigger, overMenu, clearCloseTimer]);

  const handleTriggerEnter = () => { setOverTrigger(true); clearCloseTimer(); setOpen(true); };
  const handleTriggerLeave = () => { setOverTrigger(false); maybeClose(); };
  const handleMenuEnter = () => { setOverMenu(true); clearCloseTimer(); setOpen(true); };
  const handleMenuLeave = () => { setOverMenu(false); maybeClose(); };

  return {
    buttonRef,
    open,
    setOpen,
    handleTriggerEnter,
    handleTriggerLeave,
    handleMenuEnter,
    handleMenuLeave,
  };
};

/** Avatar submenu using Popper (side-by-side, robust closing) */
const useAvatarSubmenu = () => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [open, setOpen] = useState(false);
  const closeTimer = useRef(null);

  const cancelClose = useCallback(() => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  }, []);

  const scheduleClose = useCallback(() => {
    cancelClose();
    closeTimer.current = setTimeout(() => setOpen(false), CLOSE_DELAY_MS);
  }, [cancelClose]);

  const onParentEnter = (e) => {
    setAnchorEl(e.currentTarget);
    cancelClose();
    setOpen(true);
  };

  // IMPORTANT: leaving the parent item schedules a close.
  // If the pointer is entering the submenu, its onMouseEnter will cancel this.
  const onParentLeave = () => {
    scheduleClose();
  };

  const onSubEnter = () => {
    cancelClose();
    setOpen(true);
  };

  // Leaving the submenu = close immediately (no grace)
  const onSubLeave = () => {
    cancelClose();
    setOpen(false);
  };

  return {
    anchorEl,
    open,
    setOpen,
    onParentEnter,
    onParentLeave,
    onSubEnter,
    onSubLeave,
    cancelClose,
    scheduleClose,
  };
};

const NavBar = () => {
  const navigate = useNavigate();

  const adminUser = isAdmin();

  /** Avatar root menu */
  const [avatarAnchorEl, setAvatarAnchorEl] = useState(null);
  const openAvatar = (e) => setAvatarAnchorEl(e.currentTarget);
  const closeAvatar = () => setAvatarAnchorEl(null);

  const goFromAvatar = (path) => { closeAvatar(); navigate(path); };
  
  const handleLogout = async () => {
    const token = localStorage.getItem('access_token');

    // Bust (wipe clean) the data caches on logout
    if (token) {
      try {
        // Bust both caches for the current user
        await fetch(`${BACKEND_URL}/api/dashboard/bootstrap/bust`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
        });

        await fetch(`${BACKEND_URL}/api/conversations/bust`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
        });
      } catch (e) {
        console.warn('Cache bust on logout failed (continuing logout):', e);
      }
    }

    localStorage.removeItem('access_token');
    goFromAvatar('/login');
  };


  /** Top-level Research hover menu */
  const research = useHoverMenu();

  /** Avatar submenus */
  const interviews = useAvatarSubmenu();
  const aiFeedback = useAvatarSubmenu();
  const reports = useAvatarSubmenu();
  const admin = useAvatarSubmenu(); // Admin submenu

  /** Close all avatar submenus at once */
  const closeAllAvatarSubmenus = useCallback(() => {
    interviews.setOpen(false);
    aiFeedback.setOpen(false);
    reports.setOpen(false);
    admin.setOpen(false);
    interviews.cancelClose();
    aiFeedback.cancelClose();
    reports.cancelClose();
    admin.cancelClose();
  }, [interviews, aiFeedback, reports, admin]);

  /** Helper for top-level Research navigate */
  const go = (path) => { research.setOpen(false); navigate(path); };

  /** When hovering any non-parent item inside avatar menu, close submenus */
  const handleAvatarMenuItemHover = () => {
    closeAllAvatarSubmenus();
  };

  return (
    <AppBar position="static">
      <Toolbar>
        {/* Logo */}
        <Box sx={{ display: 'flex', alignItems: 'center', mr: 2 }}>
          <img src="/CIAlogo.png" alt="Logo" style={{ height: 50, width: 'auto' }} />
        </Box>

        {/* Dashboard */}
        <MenuItem onClick={() => navigate('/dashboard')}>Dashboard</MenuItem>

        {/* Research top-level */}
        <Box
          ref={research.buttonRef}
          onMouseEnter={research.handleTriggerEnter}
          onMouseLeave={research.handleTriggerLeave}
        >
          <MenuItem>Drill Down</MenuItem>
        </Box>
        <Menu
          anchorEl={research.buttonRef.current}
          open={research.open}
          onClose={() => research.setOpen(false)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          keepMounted
          disableAutoFocusItem
          disableRestoreFocus
          PaperProps={{
            onMouseEnter: research.handleMenuEnter,
            onMouseLeave: research.handleMenuLeave,
          }}
        >

          {/* Customer Needs header + renamed item */}
          <MenuItem disabled dense sx={{ opacity: 1, cursor: 'default' }}>
            <ListItemText primary={
              <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                Customer Needs
              </Typography>
            }/>
          </MenuItem>
          <MenuItem onClick={() => go('/customer-needs')}>Jobs To Be Done</MenuItem>
          
          <Divider sx={{ my: 1 }} />

          {/* Buying Behaviors header */}
          <MenuItem disabled dense sx={{ opacity: 1, cursor: 'default' }}>
            <ListItemText primary={
              <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                Buying Behaviors
              </Typography>
            }/>
          </MenuItem>
          <MenuItem onClick={() => go('/chosen-solutions')}>Chosen Solutions</MenuItem>
          <MenuItem onClick={() => go('/previous-solutions-and-triggers')}>Previous Solutions & Switching Triggers</MenuItem>
          <MenuItem onClick={() => go('/considered-solutions')}>Considered Solutions</MenuItem>

          <Divider sx={{ my: 1 }} />

          {/* Usage Insights header + renamed item */}
          <MenuItem disabled dense sx={{ opacity: 1, cursor: 'default' }}>
            <ListItemText primary={
              <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                Usage Insights
              </Typography>
            }/>
          </MenuItem>
          <MenuItem onClick={() => go('/usage-insights')}>Pain Points & Positives</MenuItem>

          <Divider sx={{ my: 1 }} />

          {/* Tag interviews */}
          <MenuItem disabled dense sx={{ opacity: 1, cursor: 'default' }}>
            <ListItemText primary={
              <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                Create Segments
              </Typography>
            }/>
          </MenuItem>
          <MenuItem onClick={() => go('/conversations')}>Search & Tag Interviews</MenuItem>
          
        </Menu>

        {/* Interviews List */}
        <MenuItem onClick={() => navigate('/interviews')}>Interviews</MenuItem>

        {/* Spacer */}
        <Box sx={{ flexGrow: 1 }} />

        {/* Avatar (root) */}
        <IconButton onClick={openAvatar}>
          <Avatar />
        </IconButton>
        <Menu
          anchorEl={avatarAnchorEl}
          open={Boolean(avatarAnchorEl)}
          onClose={() => { closeAllAvatarSubmenus(); closeAvatar(); }}
          keepMounted
          disableAutoFocusItem
          PaperProps={{
            sx: { minWidth: 260 },
            // Leaving the entire avatar menu closes submenus
            onMouseLeave: closeAllAvatarSubmenus
          }}
        >
          {/* Interviews (Popper submenu) */}
          <MenuItem
            onMouseEnter={interviews.onParentEnter}
            onMouseLeave={interviews.onParentLeave}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
              <ListItemText primary="Interviews" />
              <ChevronRightIcon fontSize="small" />
            </Box>
          </MenuItem>
          <Popper
            open={interviews.open}
            anchorEl={interviews.anchorEl}
            placement="left-start"
            modifiers={[
              { name: 'offset', options: { offset: [12, -8] } },
              { name: 'preventOverflow', options: { boundary: 'viewport' } },
              { name: 'flip', enabled: false }
            ]}
            style={{ zIndex: 1302 }}
          >
            <Grow in={interviews.open} style={{ transformOrigin: 'left top' }}>
              <Paper
                elevation={4}
                onMouseEnter={interviews.onSubEnter}
                onMouseLeave={interviews.onSubLeave}
                sx={{ minWidth: 260, pointerEvents: 'auto' }}
              >
                <MenuList autoFocusItem={false}>
                  <MenuItem onClick={() => goFromAvatar('/subscriptions')}>Your interview subscriptions</MenuItem>
                  <MenuItem onClick={() => goFromAvatar('/managedata')}>Your self-uploaded interviews</MenuItem>
                </MenuList>
              </Paper>
            </Grow>
          </Popper>

          {/* Admin (Popper submenu) - only show if user is admin */}
          {adminUser && (
            <>
              <MenuItem
                onMouseEnter={admin.onParentEnter}
                onMouseLeave={admin.onParentLeave}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                  <ListItemText primary="Admin" />
                  <ChevronRightIcon fontSize="small" />
                </Box>
              </MenuItem>
              <Popper
                open={admin.open}
                anchorEl={admin.anchorEl}
                placement="left-start"
                modifiers={[
                  { name: 'offset', options: { offset: [12, -8] } },
                  { name: 'preventOverflow', options: { boundary: 'viewport' } },
                  { name: 'flip', enabled: false }
                ]}
                style={{ zIndex: 1302 }}
              >
                <Grow in={admin.open} style={{ transformOrigin: 'left top' }}>
                  <Paper
                    elevation={4}
                    onMouseEnter={admin.onSubEnter}
                    onMouseLeave={admin.onSubLeave}
                    sx={{ minWidth: 260 }}
                  >
                    <MenuList autoFocusItem={false}>
                      <MenuItem onClick={() => goFromAvatar('/admin/interviews')}>
                        Admin - Interviews
                      </MenuItem>
                      <MenuItem onClick={() => goFromAvatar('/admin/interview-types')}>
                        Admin - Interview Types
                      </MenuItem>
                      <MenuItem onClick={() => goFromAvatar('/admin/interview-scripts')}>
                        Admin - Interview Scripts
                      </MenuItem>
                      <MenuItem onClick={() => goFromAvatar('/admin/funnel')}>
                        Admin - Participant Funnel
                      </MenuItem>
                      <MenuItem onClick={() => goFromAvatar('/admin/organizations')}>
                        Admin - Organizations & Subscriptions
                      </MenuItem>
                      <MenuItem onClick={() => goFromAvatar('/admin/industries')}>
                        Admin - Industries
                      </MenuItem>
                      <MenuItem onClick={() => goFromAvatar('/admin/products')}>
                        Admin - Products
                      </MenuItem>
                      <MenuItem onClick={() => goFromAvatar('/admin/users')}>
                        Admin - Users
                      </MenuItem>
                    </MenuList>
                  </Paper>
                </Grow>
              </Popper>
            </>
          )}

          {/* Logout (hovering this closes submenus) – stays at bottom of top section */}
          <MenuItem onMouseEnter={handleAvatarMenuItemHover} onClick={handleLogout}>
            Logout
          </MenuItem>

          {/* Divider + Beta section header */}
          <Divider sx={{ my: 1 }} />
          <MenuItem disabled dense sx={{ opacity: 1, cursor: 'default' }}>
            <ListItemText
              primary={
                <Typography
                  variant="caption"
                  sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}
                >
                  Beta concepts (not usable yet)
                </Typography>
              }
            />
          </MenuItem>

          {/* Beta: Integrations */}
          <MenuItem
            onMouseEnter={handleAvatarMenuItemHover}
            onClick={() => goFromAvatar('/integrations')}
          >
            Integrations
          </MenuItem>

          {/* Beta: AI feedback loop (Popper submenu) */}
          <MenuItem
            onMouseEnter={aiFeedback.onParentEnter}
            onMouseLeave={aiFeedback.onParentLeave}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
              <ListItemText primary="AI feedback loop" />
              <ChevronRightIcon fontSize="small" />
            </Box>
          </MenuItem>
          <Popper
            open={aiFeedback.open}
            anchorEl={aiFeedback.anchorEl}
            placement="left-start"
            modifiers={[
              { name: 'offset', options: { offset: [12, -8] } },
              { name: 'preventOverflow', options: { boundary: 'viewport' } },
              { name: 'flip', enabled: false }
            ]}
            style={{ zIndex: 1302 }}
          >
            <Grow in={aiFeedback.open} style={{ transformOrigin: 'left top' }}>
              <Paper
                elevation={4}
                onMouseEnter={aiFeedback.onSubEnter}
                onMouseLeave={aiFeedback.onSubLeave}
                sx={{ minWidth: 260 }}
              >
                <MenuList autoFocusItem={false}>
                  {aiFeedbackSubItems.map((item) => (
                    <MenuItem key={item.label} onClick={() => goFromAvatar(item.path)}>
                      {item.label}
                    </MenuItem>
                  ))}
                </MenuList>
              </Paper>
            </Grow>
          </Popper>

          {/* Beta: Reports (Popper submenu) */}
          <MenuItem
            onMouseEnter={reports.onParentEnter}
            onMouseLeave={reports.onParentLeave}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
              <ListItemText primary="Reports" />
              <ChevronRightIcon fontSize="small" />
            </Box>
          </MenuItem>
          <Popper
            open={reports.open}
            anchorEl={reports.anchorEl}
            placement="left-start"
            modifiers={[
              { name: 'offset', options: { offset: [12, -8] } },
              { name: 'preventOverflow', options: { boundary: 'viewport' } },
              { name: 'flip', enabled: false }
            ]}
            style={{ zIndex: 1302 }}
          >
            <Grow in={reports.open} style={{ transformOrigin: 'left top' }}>
              <Paper
                elevation={4}
                onMouseEnter={reports.onSubEnter}
                onMouseLeave={reports.onSubLeave}
                sx={{ minWidth: 260 }}
              >
                <MenuList autoFocusItem={false}>
                  <MenuItem onClick={() => goFromAvatar('/strategic-report')}>Business Strategy Builder</MenuItem>
                  <MenuItem disabled>Your Reports</MenuItem>
                </MenuList>
              </Paper>
            </Grow>
          </Popper>

          {/* Beta: Personas (hovering this closes submenus) */}
          <MenuItem onMouseEnter={handleAvatarMenuItemHover} onClick={() => goFromAvatar('/personas')}>
            Personas
          </MenuItem>
        </Menu>
      </Toolbar>
    </AppBar>
  );
};

export default NavBar;
